import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
// REMOVED (2026-04-21): Service role key removed from client bundle - TM-002 security fix
// All admin operations now go through Edge Functions via AdminApiClient

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing required Supabase environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

// Import monitoring wrapper
// TEMPORARILY DISABLED to fix circular dependency causing production crash
// TODO: Re-enable after refactoring all services to use lazy loading
// import { createMonitoredClient } from '@/services/api/monitored-supabase';

// FIX: Use window global to prevent multiple GoTrueClient instances across code-split chunks
// Module-scoped variables don't work because Vite creates separate instances per chunk
// Using window ensures ALL imports share the exact same client instance
declare global {
  interface Window {
    __supabaseClient?: SupabaseClient<Database>;
    __supabaseRawClient?: SupabaseClient<Database>;
  }
}

// Detect Safari browser
const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome');
};

// Check if localStorage is available (fails in Safari Private Browsing)
const isLocalStorageAvailable = () => {
  try {
    localStorage.setItem('__test__', '1');
    localStorage.removeItem('__test__');
    return true;
  } catch (e) {
    console.error('❌ localStorage is BLOCKED:', e);
    alert('CRITICAL: localStorage blocked! Are you in Private mode?');
    return false;
  }
};

// Raw Supabase client for internal monitoring use (NO monitoring wrapper)
// This prevents infinite loops when the log buffer writes to the database
export const supabaseRaw = (() => {
  // Check window global first - ensures singleton across all code-split chunks
  if (window.__supabaseRawClient) {
    return window.__supabaseRawClient;
  }

  const safariMode = isSafari();
  const hasStorage = isLocalStorageAvailable();

  // IMPORTANT: Only apply Safari-specific workarounds if we're actually on Safari
  // Otherwise Chrome and other browsers work normally
  const shouldUseFallbackStorage = safariMode && !hasStorage;

  if (shouldUseFallbackStorage) {
    console.error('🚨 FALLBACK STORAGE MODE - Sessions will NOT persist!');
    alert('WARNING: Session persistence disabled. Sign in may not work!');
  }

  const client = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
    global: {
      headers: {
        'Cache-Control': 'no-cache'
      }
    },
    auth: {
      // Safari fix: Disable session persistence ONLY in Safari private mode
      persistSession: shouldUseFallbackStorage ? false : true,
      detectSessionInUrl: false, // Disabled - let ResetPasswordSimple component handle tokens manually
      // FIXED: Removed custom storageKey - use Supabase default (sb-*-auth-token)
      // Custom key was causing dual-storage conflicts with magic links
      autoRefreshToken: shouldUseFallbackStorage ? false : true,
      // Safari fix: Use memory storage as fallback ONLY for Safari
      storage: shouldUseFallbackStorage ? {
        getItem: (key) => null,
        setItem: () => {},
        removeItem: () => {}
      } : undefined,
      // FIX: Disable Navigator.locks API completely to prevent deadlock on Safari
      // The GoTrueClient uses locks to prevent concurrent auth operations,
      // but this can cause queries to hang indefinitely if a lock is held
      // when navigating between pages. See: https://github.com/supabase/supabase-js/issues/1620
      flowType: 'pkce', // Google dropped implicit flow support in 2024; PKCE required
      // CRITICAL FIX: Provide no-op lock to bypass Navigator.locks entirely
      // This prevents the deadlock where a previous session's lock blocks new queries
      // See: docs/bug-fixes/2025-12-20-safari-navigator-locks-deadlock.md
      lock: async (_name: string, _acquireTimeout: number, fn: () => Promise<any>) => await fn(),
    },
    db: {
      schema: 'public'
    },
    realtime: safariMode ? {
      // Safari: Reduce realtime load
      params: {
        eventsPerSecond: 2
      }
    } : undefined
  });

  // Store on window for true global singleton
  window.__supabaseRawClient = client;
  return client;
})();

// Default Supabase client for general use (Safari-optimized)
export const supabase = (() => {
  // Use window global for true singleton across all code-split chunks
  if (window.__supabaseClient) {
    return window.__supabaseClient;
  }
  window.__supabaseClient = supabaseRaw;
  return window.__supabaseClient;
})();

// REMOVED (2026-04-21): supabaseServiceRole export removed - TM-002 security fix
// All admin operations now go through Edge Functions via AdminApiClient
// See: src/services/api/admin-api.client.ts


// ✅ Factory for authenticated clients (reuses main client to prevent duplication)
export const createSupabaseClient = (accessToken?: string | null) => {
  // Reuse the main client instead of creating new instances
  return supabase;
};
